import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useAppSelector } from '../../redux/hooks';
import { activeSchemaIdSelector } from '../../redux/selectors/builder';
import { PermissionKey } from '../../utils/apollo/graphqlSchema';
import usePermission from '../../utils/hooks/permissionHooks/usePermission';
import { TextButton } from '../Button';
import CategoriesPopover from './CategoriesPopover';
import PermissionElementWrapper from '../../pages/PermissionControls/PermissionElementWrapper';
import { theme } from '../../styles/theme';
import { IEntityCategoryFieldsFragment } from '../../utils/apollo/graphqlSchema';

interface IProps {
  schemaId: string;
  onApply: (selectedCategories: string[], isManaged: boolean) => Promise<void>;
  isPermittedToAssignCategory: boolean;
  selectedEntities?: Array<{ categories: IEntityCategoryFieldsFragment[] }>;
}

const AddTagButton = styled(TextButton)<{ $isActive: boolean }>`
  padding-top: 4px;
  padding-bottom: 4px;
  ${({ $isActive }) =>
    $isActive &&
    css`
      color: ${theme.colors.mainBlue};
    `}
`;

const GlobalCategories = ({
  schemaId,
  onApply,
  isPermittedToAssignCategory,
  selectedEntities = [],
}: IProps) => {
  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadCategory = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.CategoryRead],
    schemaId: activeSchemaId,
  });

  const onComplete = async (
    selectedCategories: string[],
    isManaged: boolean,
  ) => {
    await onApply(selectedCategories, isManaged);
    setOpen(false);
  };

  // If there's exactly one dictionary entity item selected,
  // show the tags of that entity in the popover
  const entityCategories =
    selectedEntities.length === 1 ? selectedEntities[0].categories : [];

  return (
    <div>
      <PermissionElementWrapper
        isPermitted={isPermittedToAssignCategory && isPermittedToReadCategory}
      >
        <AddTagButton
          ref={anchorEl}
          type="button"
          onClick={() => setOpen(true)}
          disabled={!isPermittedToAssignCategory || !isPermittedToReadCategory}
          trackContext="categories"
          trackAction="apply global tags"
          $isActive={open}
        >
          + Apply Tags
        </AddTagButton>
      </PermissionElementWrapper>
      {open && (
        <CategoriesPopover
          schemaId={schemaId}
          anchorEl={anchorEl}
          isOpen={true}
          onClose={() => setOpen(false)}
          onApply={onComplete}
          entityCategories={entityCategories}
        />
      )}
    </div>
  );
};

export default GlobalCategories;
